import { useState, useEffect, useRef, type RefObject } from 'react'

interface UseImageLoadAndVisibleOptions {
  onImageLoadAndVisible?: () => void
  preloaded?: boolean
  // Kept for API compatibility — no longer used
  threshold?: number
  rootMargin?: string
  fallbackTimeout?: number
}

interface UseImageLoadAndVisibleReturn {
  imageLoaded: boolean
  imgRef: RefObject<HTMLImageElement | null>
  handleImageLoad: () => void
  handleImageError: () => void
  imageVisible: boolean
}

export function useImageLoadAndVisible({
  onImageLoadAndVisible,
  preloaded = false,
}: UseImageLoadAndVisibleOptions = {}): UseImageLoadAndVisibleReturn {
  const [imageLoaded, setImageLoaded] = useState(preloaded)
  const [imageVisible, setImageVisible] = useState(preloaded)
  const imgRef = useRef<HTMLImageElement>(null)
  const hasSignaledLoad = useRef(false)

  // Store latest callback in a ref so the mount effect never goes stale
  const onImageLoadAndVisibleRef = useRef(onImageLoadAndVisible)
  onImageLoadAndVisibleRef.current = onImageLoadAndVisible

  // Handle images already in browser cache — onLoad won't fire if img.complete is already true
  useEffect(() => {
    if (hasSignaledLoad.current || !imgRef.current) return
    if (imgRef.current.complete && imgRef.current.naturalWidth > 0) {
      setImageLoaded(true)
      setImageVisible(true)
      if (onImageLoadAndVisibleRef.current) {
        hasSignaledLoad.current = true
        onImageLoadAndVisibleRef.current()
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // mount only — ref gives us the latest callback without re-running

  const handleImageLoad = async () => {
    try {
      if (imgRef.current) await imgRef.current.decode()
    } catch {
      // decode() unsupported or element unmounted — proceed anyway
    }
    setImageLoaded(true)
    setImageVisible(true)
    if (!hasSignaledLoad.current && onImageLoadAndVisibleRef.current) {
      hasSignaledLoad.current = true
      onImageLoadAndVisibleRef.current()
    }
  }

  // Treat a load error as "done" so the parent counter isn't permanently stuck.
  // imageVisible stays false so the broken image is hidden, but the skeleton can lift.
  const handleImageError = () => {
    if (!hasSignaledLoad.current && onImageLoadAndVisibleRef.current) {
      hasSignaledLoad.current = true
      onImageLoadAndVisibleRef.current()
    }
  }

  return { imageLoaded, imageVisible, imgRef, handleImageLoad, handleImageError }
}
